import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Currency from "@mapmycustomers/shared/types/Currency";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import Organization from "@mapmycustomers/shared/types/Organization";
import CustomMetric from "@mapmycustomers/shared/types/stackRank/CustomMetric";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { getCurrencies } from "@app/store/referenceData";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import { convertCustomMetricIntoCustomField } from "@app/util/stackRank/rankingFields";

import {
  createCustomMetric,
  deleteCustomMetric,
  fetchCustomMetrics,
  updateCustomMetric,
} from "./actions";
import { getCustomMetrics } from "./selectors";

function* updateLeaderboardFieldModel(customMetrics: CustomMetric[]) {
  const currencies: Currency[] = yield select(getCurrencies);
  const defaultCurrencyCode = localSettings.getCurrencyCode();
  const defaultCurrencyId = currencies.find(({ code }) => code === defaultCurrencyCode)!.id;

  const customFields = customMetrics.reduce((result, metric) => {
    const customField = convertCustomMetricIntoCustomField(metric, defaultCurrencyId);
    if (customField) {
      result.push(customField);
    }
    return result;
  }, [] as CustomField[]);

  if (customFields) {
    leaderboardFieldModel.setCustomFields(customFields, [FieldFeature.METRIC]);
  }
}

export function* onFetchCustomMetrics() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<CustomMetric> = yield callApi("fetchStackRankMetrics", orgId);

    // must be called before "success" action
    yield call(updateLeaderboardFieldModel, response.data);

    yield put(fetchCustomMetrics.success(response.data));
  } catch (error) {
    yield put(fetchCustomMetrics.failure());
    yield put(handleError({ error }));
  }
}

function* onCreateCustomMetric({ payload }: ReturnType<typeof createCustomMetric.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: CustomMetric = yield callApi(
      "createStackRankMetric",
      orgId,
      payload.customMetric
    );

    let customMetrics: CustomMetric[] = yield select(getCustomMetrics);
    customMetrics = [...customMetrics, response];
    // must be called before "success" action
    yield call(updateLeaderboardFieldModel, customMetrics);
    yield put(createCustomMetric.success(response));

    if (payload.successCallback) {
      yield call(payload.successCallback, response);
    }
  } catch (error) {
    yield put(createCustomMetric.failure());
    yield put(handleError({ error }));
  }
}

function* onUpdateCustomMetric({ payload }: ReturnType<typeof updateCustomMetric.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: CustomMetric = yield callApi(
      "updateStackRankMetric",
      orgId,
      payload.customMetric
    );

    let customMetrics: CustomMetric[] = yield select(getCustomMetrics);
    customMetrics = customMetrics.map((metric) => (metric.id === response.id ? response : metric));
    // must be called before "success" action
    yield call(updateLeaderboardFieldModel, customMetrics);

    yield put(updateCustomMetric.success(response));
    if (payload.successCallback) {
      yield call(payload.successCallback, response);
    }
  } catch (error) {
    yield put(updateCustomMetric.failure());
    yield put(handleError({ error }));
  }
}

function* onDeleteCustomMetric({ payload }: ReturnType<typeof deleteCustomMetric.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteStackRankMetric", orgId, payload.customMetricId);

    let customMetrics: CustomMetric[] = yield select(getCustomMetrics);
    customMetrics = customMetrics.filter(({ id }) => id !== payload.customMetricId);
    // must be called before "success" action
    yield call(updateLeaderboardFieldModel, customMetrics);

    yield put(deleteCustomMetric.success(payload.customMetricId));
    if (payload.successCallback) {
      yield call(payload.successCallback, payload.customMetricId);
    }
  } catch (error) {
    yield put(deleteCustomMetric.failure());
    yield put(handleError({ error }));
  }
}

export function* stackRankSaga() {
  yield takeLatest(fetchCustomMetrics.request, onFetchCustomMetrics);
  yield takeEvery(createCustomMetric.request, onCreateCustomMetric);
  yield takeEvery(updateCustomMetric.request, onUpdateCustomMetric);
  yield takeEvery(deleteCustomMetric.request, onDeleteCustomMetric);
}
